import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import { ASSIGNMENT_HISTORY_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { formatDateTime } from "../../utils/datetime";
import { DialogModal } from "../ui/dialogModal";
import OwnerHistory from "./OwnerHistory";
import { METHOD_LABELS, MethodPill, inputCls } from "./shared";

/** Every owner change, newest first, with how and why. */
export default function HistoryTab({ users, teams }) {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({ page: 1, num_pages: 1, count: 0 });
    const [filters, setFilters] = useState({ method: "", user_id: "", team_id: "", date_from: "", date_to: "", lead: "" });
    const [openLead, setOpenLead] = useState(null);

    const load = (page = 1) => fetch(ASSIGNMENT_HISTORY_URL, { ...filters, page, page_size: 50 }).then(res => {
        if (res?.status === "success") {
            setRows(res.results);
            setMeta({ page: res.page, num_pages: res.num_pages, count: res.count });
        } else setRows([]);
    });
    useEffect(() => { load(1); }, [filters.method, filters.user_id, filters.team_id, filters.date_from, filters.date_to]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
                <input placeholder="Lead name…" value={filters.lead} onChange={e => set("lead", e.target.value)}
                    onKeyDown={e => e.key === "Enter" && load(1)} className={cn(inputCls, "w-40")} />
                <select value={filters.method} onChange={e => set("method", e.target.value)} className={cn(inputCls, "w-36")}>
                    <option value="">Any method</option>
                    {Object.entries(METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={filters.user_id} onChange={e => set("user_id", e.target.value)} className={cn(inputCls, "w-40")}>
                    <option value="">Anyone</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <select value={filters.team_id} onChange={e => set("team_id", e.target.value)} className={cn(inputCls, "w-36")}>
                    <option value="">Any team</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <input type="date" value={filters.date_from} onChange={e => set("date_from", e.target.value)} className={cn(inputCls, "w-36")} aria-label="From" />
                <input type="date" value={filters.date_to} onChange={e => set("date_to", e.target.value)} className={cn(inputCls, "w-36")} aria-label="To" />
                <span className="text-[11px] text-gray-400 ml-auto">{meta.count} change{meta.count === 1 ? "" : "s"}</span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                        <tr>{["When", "Lead", "From", "To", "How", "Rule", "By", "Reason"].map(h =>
                            <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {(rows || []).map(r => (
                            <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer" onClick={() => setOpenLead(r)}>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">{formatDateTime(r.when)}</td>
                                <td className="px-3 py-2">
                                    <button type="button" className="text-primary hover:underline text-left"
                                        onClick={e => { e.stopPropagation(); navigate(`/customers/customerdetails?id=${r.lead_id}`, { state: { formEdit: true, row: { id: r.lead_id } } }); }}>
                                        {r.lead_name || `Lead #${r.lead_id}`}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-gray-500">{r.from?.name || "Unassigned"}</td>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{r.to?.name || "Unassigned"}</td>
                                <td className="px-3 py-2"><MethodPill method={r.method} /></td>
                                <td className="px-3 py-2 text-gray-500">{r.rule ? `${r.rule.name} v${r.rule.version}` : "—"}</td>
                                <td className="px-3 py-2 text-gray-500">{r.by?.name}</td>
                                <td className="px-3 py-2 text-gray-500 max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
                            </tr>
                        ))}
                        {rows && !rows.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No owner changes match</td></tr>}
                        {!rows && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
                    </tbody>
                </table>
            </div>
            {meta.num_pages > 1 && (
                <div className="flex items-center justify-end gap-2 text-xs">
                    <button disabled={meta.page <= 1} onClick={() => load(meta.page - 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
                    <span className="text-gray-500">Page {meta.page} of {meta.num_pages}</span>
                    <button disabled={meta.page >= meta.num_pages} onClick={() => load(meta.page + 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
                </div>
            )}
            <DialogModal open={!!openLead} onClose={() => setOpenLead(null)} showActions={false} width="820px"
                title={openLead ? `Owner history — ${openLead.lead_name || `Lead #${openLead.lead_id}`}` : ""}>
                {openLead && <OwnerHistory leadId={openLead.lead_id} />}
            </DialogModal>
        </div>
    );
}
